import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { filledBranchDoc, sameBranchDoc } from '../../lib/branchConfig'
import { carryOver, localProblems, saveOutcome } from '../../lib/equipment'
import { explain, refusedWith } from '../../lib/staffErrors'
import type { BranchDoc, EquipmentDoc, ProfileProblem, ProfileUnitKind, UnitSettings } from '../../lib/types'
import { useMayRead } from '../../ui/Session'
import { useStaffText } from '../../ui/StaffText'
import { setupChanged } from '../setupChanged'

export type CarryPreview = ReturnType<typeof carryOver>

export interface DraftEquipment {
  id: string
  revision: number
  document: EquipmentDoc
  kindDefaults: Record<ProfileUnitKind, UnitSettings>
}

const NO_EQUIPMENT: EquipmentDoc = { units: [] }

async function readEquipment(id: string): Promise<DraftEquipment | null> {
  if (!id) return null
  const a = await api.equipmentDocument(id)
  return { id, revision: a.revision, document: { units: a.document.units ?? [] }, kindDefaults: a.kindDefaults }
}

/**
 * A branch's setup held on the page and saved whole; a save is live. A profile switch
 * is previewed here with the carry-over mirror, then saved at once with the units
 * unchanged and the new pointers: the server carries the instances over (R4).
 */
/** What the server's answer to an applied switch reports, from its warnings. */
export interface SwitchReport {
  dropped: string[]
  clearedDesigns: string[]
  notMade: { unit: string; count: number }[]
}

export function switchReport(warnings: readonly ProfileProblem[]): SwitchReport | null {
  const arg = (p: ProfileProblem, k: string) => p.args?.[k]
  const r: SwitchReport = { dropped: [], clearedDesigns: [], notMade: [] }
  for (const p of warnings) {
    if (p.code === 'INSTANCE_DROPPED') r.dropped.push(String(arg(p, 'code') ?? ''))
    else if (p.code === 'KIOSK_DESIGN_CLEARED') r.clearedDesigns.push(String(arg(p, 'code') ?? ''))
    else if (p.code === 'INSTANCES_NOT_MADE') r.notMade.push({ unit: String(arg(p, 'unit') ?? ''), count: Number(arg(p, 'count') ?? 0) })
  }
  return r.dropped.length || r.clearedDesigns.length || r.notMade.length ? r : null
}

export function useBranchDraft(branchId: string, organisation?: string) {
  const { lang } = useStaffText()
  const readsLayouts = useMayRead('layouts')
  const [saved, setSaved] = useState<BranchDoc | null>(null)
  const [draft, setDraftState] = useState<BranchDoc | null>(null)
  const [baseRevision, setBaseRevision] = useState(0)
  const [equipment, setEquipment] = useState<DraftEquipment | null>(null)
  const [serverProblems, setServerProblems] = useState<ProfileProblem[]>([])
  const [stale, setStale] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [pending, setPending] = useState<{ preview: CarryPreview; equipment: DraftEquipment } | null>(null)
  // What the server did on the last applied switch, until dismissed.
  const [report, setReport] = useState<SwitchReport | null>(null)
  const seq = useRef(0)

  const reload = useCallback(async () => {
    const mine = ++seq.current
    setError('')
    try {
      const answer = await api.branchSetup(branchId)
      const doc = filledBranchDoc(answer.config.draft)
      const eq = await readEquipment(doc.equipmentProfileId)
      if (mine !== seq.current) return
      setSaved(doc)
      setDraftState(doc)
      setBaseRevision(answer.config.revision)
      setEquipment(eq)
      setServerProblems(answer.warnings ?? [])
      setStale('')
      setPending(null)
      setReport(null)
    } catch (err) {
      if (mine === seq.current) setError(explain(err, lang, 'admin.publish.notRead'))
    }
    // lang is read only for the sentence.
  }, [branchId])

  useEffect(() => {
    void reload()
  }, [reload])

  const setDraft = useCallback((next: BranchDoc | ((d: BranchDoc) => BranchDoc)) => {
    setDraftState((d) => (d === null ? d : typeof next === 'function' ? next(d) : next))
  }, [])

  /** Saves a document; a refusal is said on the page unless `quiet`, and returned. */
  const store = async (
    doc: BranchDoc,
    after?: DraftEquipment,
    quiet = false,
  ): Promise<{ warnings: ProfileProblem[] } | { refused: string }> => {
    setBusy(true)
    setError('')
    setStale('')
    try {
      const a = await api.saveBranchSetup(branchId, { config: doc, baseRevision })
      const next = filledBranchDoc(a.draft ?? doc)
      setSaved(next)
      setDraftState(next)
      setBaseRevision(a.revision)
      setServerProblems(a.warnings ?? [])
      if (after) setEquipment(after)
      if (a.overwrote) setStale(a.overwrote)
      window.dispatchEvent(setupChanged())
      return { warnings: a.warnings ?? [] }
    } catch (err) {
      const refused = refusedWith(err, lang, 'eq.branch.notSaved')
      const o = saveOutcome(err)
      if (o.kind === 'problems') setServerProblems(o.problems)
      else if (!quiet) setError(refused)
      return { refused }
    } finally {
      setBusy(false)
    }
  }

  const save = async (): Promise<boolean> => {
    if (!draft) return false
    const ok = 'warnings' in (await store(draft))
    if (ok) setReport(null)
    return ok
  }

  /** One inner page's change, saved with the rest of the page: '' when saved, else the refusal. */
  const commit = async (change: (d: BranchDoc) => BranchDoc): Promise<string> => {
    if (!draft) return ''
    const r = await store(change(draft), undefined, true)
    if ('refused' in r) return r.refused
    setReport(null)
    return ''
  }

  /** The target operation profile's kiosk designs; undefined when they cannot be read. */
  const kioskDesignsOf = async (opId: string): Promise<Set<string> | undefined> => {
    if (!readsLayouts) return undefined
    try {
      const { layouts } = await api.layouts(organisation)
      return new Set(layouts.filter((l) => l.kind === 'kiosk' && l.operationProfileId === opId).map((l) => l.id))
    } catch {
      return undefined
    }
  }

  /** Loads the target profiles and previews the carry-over; nothing is applied. */
  const switchProfiles = async (opId: string, eqId?: string): Promise<CarryPreview | null> => {
    if (!draft) return null
    setError('')
    try {
      const rows = (await api.equipmentProfiles(opId)).equipmentProfiles
      const row = eqId ? rows.find((r) => r.id === eqId) : rows[0]
      if (!row) return null
      const [target, kioskDesigns] = await Promise.all([
        readEquipment(row.id),
        opId === draft.operationProfileId ? Promise.resolve(undefined) : kioskDesignsOf(opId),
      ])
      if (!target) return null
      const preview = carryOver(draft, equipment?.document ?? NO_EQUIPMENT, {
        operationProfileId: opId,
        equipmentProfileId: row.id,
        document: target.document,
        kioskDesigns,
      })
      setPending({ preview, equipment: target })
      return preview
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      return null
    }
  }

  /** Saves the pending switch: the units as they are, the new pointers (R4). */
  const applySwitch = async (): Promise<boolean> => {
    if (!pending || !draft) return false
    const { next } = pending.preview
    const r = await store(
      { ...draft, operationProfileId: next.operationProfileId, equipmentProfileId: next.equipmentProfileId },
      pending.equipment,
    )
    if ('refused' in r) return false
    setPending(null)
    setReport(switchReport(r.warnings))
    return true
  }

  const cancelSwitch = () => setPending(null)

  const dirty = draft !== null && saved !== null && !sameBranchDoc(draft, saved)
  const local = draft && equipment ? localProblems(draft, equipment.document, lang) : []
  const seen = new Set(local.map((p) => `${p.path} ${p.code}`))
  const problems = [...local, ...serverProblems.filter((p) => !seen.has(`${p.path} ${p.code}`))]

  return {
    draft,
    baseRevision,
    equipment,
    dirty,
    /** Local checks and the server's last answer (422 problems or save warnings). */
    problems,
    localProblems: local,
    /** Who this person's last save was written over, or empty. */
    stale,
    error,
    busy,
    pending: pending?.preview ?? null,
    /** What the server reported dropping, clearing or not making on the last applied switch. */
    report,
    dismissReport: () => setReport(null),
    pendingTarget: pending ? { operationProfileId: pending.preview.next.operationProfileId, equipmentProfileId: pending.equipment.id } : null,
    setDraft,
    switchProfiles,
    applySwitch,
    cancelSwitch,
    save,
    commit,
    reload,
  }
}

export type BranchDraft = ReturnType<typeof useBranchDraft>
